/**
 * ege-stress-core.js — логика страницы «Ударения ЕГЭ» без DOM: формы списка,
 * задание 4 в формате экзамена, карточки с интервальным повтором, тест.
 *
 * Данные — public/data/lang/ru-orthoepy-ege-2026.json («Орфоэпический список –
 * 2026» ФИПИ). Тот же файл — эталон, по которому сверяются оба лейна ударений
 * (tests/js/russian-stress-norm.test.mjs), поэтому страница не может учить
 * ударению, которого не знает озвучка сайта, и наоборот.
 *
 * Правила экзамена, которые знает этот файл (ФИПИ, навигатор 2026):
 *   · задание 4 — пять слов с выделенной буквой, в ответ идут номера ВСЕХ слов,
 *     где ударная гласная выделена верно, 1 балл только за точный ответ;
 *   · «буква Ё может быть обозначена как Е»: «свеклА» — неверно.
 *
 * Browser: window.EgeStressCore · Node: module.exports (tests/js/ege-stress-core.test.mjs)
 */
(function (root, factory) {
    var mod = factory();
    if (typeof module !== 'undefined' && module.exports) module.exports = mod;
    if (typeof window !== 'undefined') window.EgeStressCore = mod;
})(this, function () {
    'use strict';

    var ACUTE = '́';
    var VOWELS = 'аеёиоуыэюя';
    var DAY = 86400000;

    function isVowel(ch) { return VOWELS.indexOf(ch) >= 0; }

    /** Номер ударной гласной формы (акут после гласной или «ё»), -1 — нет. */
    function stressOrdinal(form) {
        var low = String(form).toLowerCase(), n = 0;
        for (var i = 0; i < low.length; i++) {
            if (!isVowel(low[i])) continue;
            if (low[i + 1] === ACUTE || low[i] === 'ё') return n;
            n++;
        }
        return -1;
    }

    function plainOf(form) { return String(form).split(ACUTE).join(''); }

    function vowelCount(low) {
        var n = 0;
        for (var i = 0; i < low.length; i++) if (isVowel(low[i])) n++;
        return n;
    }

    /**
     * Все ударные формы списка, у которых больше одной гласной, — по одной
     * на пару «слово + ударение». `hint` — строка списка без ударений (её
     * заглавные гласные — это и есть ответ) и без «ё» (ё ударна сама).
     * `alt` — чтения того же слова, которые тоже верны, но в ДРУГОМ значении
     * (о́тзыв / отзы́в, сиро́ты / сироты́): их нельзя показывать как ошибку.
     */
    function items(data) {
        var out = [], seen = {};
        var homo = (data && data.homographs) || {};
        (data.sections || []).forEach(function (s) {
            (s.entries || []).forEach(function (e) {
                (e.forms || []).forEach(function (f) {
                    var plain = plainOf(f), low = plain.toLowerCase();
                    var nv = vowelCount(low);
                    if (nv < 2) return;
                    var ord = stressOrdinal(f);
                    if (ord < 0) return;
                    var key = low.replace(/ё/g, 'е');
                    var id = key + ':' + ord;
                    if (seen[id]) return;
                    seen[id] = 1;
                    out.push({
                        id: id, key: key, form: f, low: low, ord: ord, vowels: nv,
                        yo: low.indexOf('ё') >= 0,
                        section: s.id, line: e.line,
                        hint: String(e.line).toLowerCase().replace(/ё/g, 'е'),
                        alt: [],
                        homograph: Object.prototype.hasOwnProperty.call(homo, key),
                    });
                });
            });
        });
        // второе чтение омографа из самого списка (о́тзыв и отзы́в — обе строки есть)
        var byKey = {};
        out.forEach(function (it) { (byKey[it.key] = byKey[it.key] || []).push(it); });
        out.forEach(function (it) {
            byKey[it.key].forEach(function (o) { if (o !== it) it.alt.push(o.ord); });
            var h = homo[it.key];
            if (h && typeof h === 'object' && Array.isArray(h.other)) {
                h.other.forEach(function (f) { var o = stressOrdinal(f); if (o >= 0 && o !== it.ord && it.alt.indexOf(o) < 0) it.alt.push(o); });
            }
        });
        return out;
    }

    /**
     * Слово так, как его печатает экзамен: строчными, выбранная гласная —
     * заглавной. «ё» заглавной остаётся «Ё», незаглавная печатается как «е»
     * (ФИПИ: «буква Ё может быть обозначена как буква Е»).
     */
    function shown(item, ord) {
        var out = '', n = -1;
        for (var i = 0; i < item.low.length; i++) {
            var ch = item.low[i];
            if (isVowel(ch)) {
                n++;
                if (n === ord) { out += ch.toUpperCase(); continue; }
                if (ch === 'ё') { out += 'е'; continue; }
            }
            out += ch;
        }
        return out;
    }

    /** То же слово без ударения — для карточки (ё печатается как е). */
    function bare(item) { return item.low.replace(/ё/g, 'е'); }

    /** Позиции гласных слова в строке bare(item) — кнопки карточки. */
    function vowelSlots(item) {
        var b = bare(item), out = [];
        for (var i = 0; i < b.length; i++) if (isVowel(b[i])) out.push(i);
        return out;
    }

    /**
     * Неверное ударение для задания: соседняя гласная — первой (так ошибаются
     * люди: позва́ла, све́рлит), никогда — чтение, верное в другом значении.
     * -1 — неверного нет (у «на́чал / нача́л» обе гласные — слова).
     */
    function wrongOrd(item, rnd) {
        var near = [], far = [];
        for (var v = 0; v < item.vowels; v++) {
            if (v === item.ord || item.alt.indexOf(v) >= 0) continue;
            (Math.abs(v - item.ord) === 1 ? near : far).push(v);
        }
        var pool = near.length && (rnd() < 0.8 || !far.length) ? near : far;
        return pool.length ? pool[Math.floor(rnd() * pool.length)] : -1;
    }

    function shuffle(arr, rnd) {
        var a = arr.slice();
        for (var i = a.length - 1; i > 0; i--) {
            var j = Math.floor(rnd() * (i + 1)), t = a[i];
            a[i] = a[j]; a[j] = t;
        }
        return a;
    }

    /**
     * Задание 4: пять слов из РАЗНЫХ строк списка, верных от одного до четырёх
     * (в демоверсиях ответы «24», «145», «1245»). answer — номера верных по
     * возрастанию одной строкой, как в бланке.
     */
    function examTask(list, rnd) {
        rnd = rnd || Math.random;
        var byLine = {}, lines = [];
        list.forEach(function (it) {
            if (!byLine[it.line]) { byLine[it.line] = []; lines.push(it.line); }
            byLine[it.line].push(it);
        });
        var chosen = shuffle(lines, rnd).slice(0, 5).map(function (l) {
            var group = byLine[l];
            return group[Math.floor(rnd() * group.length)];
        });
        var r = rnd();
        var k = r < 0.15 ? 1 : r < 0.5 ? 2 : r < 0.85 ? 3 : 4;
        var order = shuffle([0, 1, 2, 3, 4], rnd);
        var right = {};
        // слово без неверного варианта может быть только верным
        chosen.forEach(function (it, i) { if (wrongOrd(it, function () { return 0; }) < 0) right[i] = 1; });
        for (var j = 0; j < order.length && Object.keys(right).length < k; j++) right[order[j]] = 1;
        var words = chosen.map(function (it, i) {
            var ok = !!right[i];
            var ord = ok ? it.ord : wrongOrd(it, rnd);
            return { item: it, ord: ord, right: ok, text: shown(it, ord) };
        });
        var answer = words.map(function (w, i) { return w.right ? String(i + 1) : ''; }).join('');
        return { words: words, answer: answer };
    }

    /** Точное совпадение набора номеров — как проверяет экзамен (порядок цифр не важен). */
    function checkExam(task, picked) {
        var got = (picked || []).map(Number).filter(function (n) { return n >= 1 && n <= 5; })
            .sort().filter(function (n, i, a) { return a.indexOf(n) === i; }).join('');
        return { point: got === task.answer ? 1 : 0, given: got, answer: task.answer };
    }

    // ── карточки: коробки Лейтнера ─────────────────────────────────────────
    // Коробка 0 — новое или ошибка: вернётся через три карточки (не подряд —
    // так ответ не угадывается по памяти мгновения). Дальше 1, 3, 7, 21 день.
    var BOX_DAYS = [0, 1, 3, 7, 21];
    var GAP = 3;

    function emptyState() { return { v: 1, cards: {}, recent: [] }; }

    function nextCard(state, list, now, rnd, filter) {
        rnd = rnd || Math.random;
        var pool = filter ? list.filter(filter) : list;
        if (!pool.length) return null;
        var recent = state.recent || [];
        var due = [], fresh = [], later = [];
        pool.forEach(function (it) {
            if (recent.indexOf(it.id) >= 0 && pool.length > GAP) return;
            var c = state.cards[it.id];
            if (!c) fresh.push(it);
            else if (c.due <= now) due.push({ it: it, c: c });
            else later.push({ it: it, c: c });
        });
        if (due.length) {
            due.sort(function (a, b) { return a.c.b - b.c.b || a.c.due - b.c.due; });
            return due[0].it;
        }
        if (fresh.length) return fresh[Math.floor(rnd() * fresh.length)];
        if (later.length) {
            later.sort(function (a, b) { return a.c.due - b.c.due; });
            return later[0].it;
        }
        return pool[Math.floor(rnd() * pool.length)];
    }

    function grade(state, id, ok, now) {
        var c = state.cards[id] || { b: 0, due: 0, n: 0, w: 0 };
        c.n++;
        if (ok) {
            c.b = Math.min(c.b + 1, BOX_DAYS.length - 1);
            c.due = now + BOX_DAYS[c.b] * DAY;
        } else {
            c.b = 0; c.w++;
            c.due = now;                       // вернётся после GAP других карточек
        }
        state.cards[id] = c;
        state.recent = (state.recent || []).concat([id]).slice(-GAP);
        return c;
    }

    /** Сводка для экрана: сколько слов уже в долгих коробках, сколько с ошибкой. */
    function progress(state, list) {
        var sure = 0, weak = 0, seen = 0;
        list.forEach(function (it) {
            var c = state.cards[it.id];
            if (!c) return;
            seen++;
            if (c.b >= 2) sure++;
            if (c.b === 0 && c.w > 0) weak++;
        });
        return { total: list.length, seen: seen, sure: sure, weak: weak };
    }

    /** Состояние из хранилища — всё, что не похоже на наше, выбрасывается. */
    function restore(raw) {
        try {
            var s = typeof raw === 'string' ? JSON.parse(raw) : raw;
            if (!s || s.v !== 1 || typeof s.cards !== 'object') return emptyState();
            var clean = emptyState();
            for (var id in s.cards) {
                if (!Object.prototype.hasOwnProperty.call(s.cards, id)) continue;
                var c = s.cards[id];
                if (!c || typeof c.b !== 'number' || typeof c.due !== 'number') continue;
                clean.cards[id] = { b: Math.max(0, Math.min(c.b | 0, BOX_DAYS.length - 1)), due: +c.due || 0, n: c.n | 0, w: c.w | 0 };
            }
            return clean;
        } catch (_) { return emptyState(); }
    }

    /**
     * Тест из N слов: каждая часть речи хотя бы раз, остальное — долями разделов
     * в самом списке (глаголов больше всех), каждая строка списка — не больше
     * одного раза.
     */
    function testSet(list, n, rnd) {
        rnd = rnd || Math.random;
        n = n || 20;
        var bySection = {}, order = [];
        shuffle(list, rnd).forEach(function (it) {
            if (!bySection[it.section]) { bySection[it.section] = []; order.push(it.section); }
            bySection[it.section].push(it);
        });
        var quota = {}, sum = 0;
        order.forEach(function (s, i) { quota[s] = i < n ? 1 : 0; sum += quota[s]; });
        var rest = Math.max(0, n - sum), total = list.length;
        var shares = order.map(function (s) {
            var exact = rest * bySection[s].length / total;
            return { s: s, base: Math.floor(exact), frac: exact - Math.floor(exact) };
        });
        shares.forEach(function (x) { quota[x.s] += x.base; sum += x.base; });
        shares.sort(function (a, b) { return b.frac - a.frac; });
        for (var i = 0; sum < n && i < shares.length; i++) { quota[shares[i].s]++; sum++; }
        var out = [], usedLine = {};
        order.forEach(function (s) {
            var want = quota[s];
            bySection[s].forEach(function (it) {
                if (want <= 0 || usedLine[it.line]) return;
                usedLine[it.line] = 1; out.push(it); want--;
            });
        });
        return shuffle(out, rnd);
    }

    return {
        items: items, shown: shown, bare: bare, vowelSlots: vowelSlots, stressOrdinal: stressOrdinal,
        examTask: examTask, checkExam: checkExam, wrongOrd: wrongOrd,
        emptyState: emptyState, nextCard: nextCard, grade: grade, progress: progress, restore: restore,
        testSet: testSet, BOX_DAYS: BOX_DAYS,
    };
});
